import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { createURN } from 'garaje/models/generic-resource';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { ResourceBookingRule } from 'garaje/types/generic-resources/ResourceTypeConfig';
import type { ResourceTypeConfig } from 'garaje/types/generic-resources/ResourceTypeConfig';
import urlBuilder from 'garaje/utils/url-builder';

interface PartialDayPanelArgs {
  resourceTypeId: number;
  resourceTypeConfig: ResourceTypeConfig;
}

const FULL_DAY_RESERVATION_STRATEGY = {
  key: ResourceBookingRule.RESERVATION_TYPE,
  type: 'FullDayReservationStrategy',
  properties: {},
};

const PARTIAL_DAY_RESERVATION_STRATEGY = {
  key: ResourceBookingRule.RESERVATION_TYPE,
  type: 'PartialDayReservationStrategy',
  properties: {},
};

export default class ResourceSettingsPartialDay extends Component<PartialDayPanelArgs> {
  @service declare ajax: AjaxFetchService;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;

  @tracked isPartialDayEnabled: boolean = false;

  constructor(owner: unknown, args: PartialDayPanelArgs) {
    super(owner, args);

    this.isPartialDayEnabled = this.isCurrentlyPartialDay;
  }

  get isCurrentlyPartialDay(): boolean {
    const { bookingRules } = this.args.resourceTypeConfig;
    const reservationTypeStrategy = bookingRules.find((rule) => rule.key === ResourceBookingRule.RESERVATION_TYPE);

    return reservationTypeStrategy?.type === 'PartialDayReservationStrategy';
  }

  getNonReservationTypeBookingRules(): ResourceTypeConfig['bookingRules'] {
    const { bookingRules } = this.args.resourceTypeConfig;
    return bookingRules.filter((rule) => rule.key !== ResourceBookingRule.RESERVATION_TYPE);
  }

  @action
  async handlePartialDayToggle(): Promise<void> {
    const updatedBookingRules = this.isPartialDayEnabled
      ? this.setFullDayReservationStrategy()
      : this.setPartialDayReservationStrategy();

    try {
      await this.updateReservationTypeStrategy.perform(updatedBookingRules);
      this.isPartialDayEnabled = !this.isPartialDayEnabled;
    } catch (_) {
      this.flashMessages.showAndHideFlash('error', 'Could not update partial day booking setting');
    }
  }

  setFullDayReservationStrategy = (): ResourceTypeConfig['bookingRules'] => {
    return [
      ...this.getNonReservationTypeBookingRules(),
      FULL_DAY_RESERVATION_STRATEGY,
    ] as ResourceTypeConfig['bookingRules'];
  };

  setPartialDayReservationStrategy = (): ResourceTypeConfig['bookingRules'] => {
    return [
      ...this.getNonReservationTypeBookingRules(),
      PARTIAL_DAY_RESERVATION_STRATEGY,
    ] as ResourceTypeConfig['bookingRules'];
  };

  updateReservationTypeStrategy = task(
    { drop: true },
    async (updatedBookingRules: ResourceTypeConfig['bookingRules']) => {
      const url = urlBuilder.rms.resourceTypes();

      await this.ajax.request(url, {
        method: 'PUT',
        contentType: 'application/json',
        data: JSON.stringify({
          data: {
            ...this.args.resourceTypeConfig,
            relationId: createURN('location', this.state.currentLocation.id),
            bookingRules: updatedBookingRules,
          },
        }),
      });
    },
  );
}
